import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

/**
 * RGPD/GDPR Compliance utilities
 * Gestion de la conformité RGPD pour les données utilisateur
 */

export interface DataExportRequest {
  userId: string;
  requestDate: Date;
  email: string;
}

export interface DataDeletionRequest {
  userId: string;
  requestDate: Date;
  reason?: string;
}

/**
 * Exporte toutes les données utilisateur (RGPD Article 20)
 */
export async function exportUserData(userId: string): Promise<any> {
  try {
    const { data, error } = await supabase
      .rpc('export_user_data_gdpr', { target_user_id: userId });
    if (error) throw error;
    return data;
  } catch (error) {
    logger.error("Erreur lors de l'export des données:", toError(error).message);
    throw new Error("Impossible d'exporter les données utilisateur");
  }
}

/**
 * Supprime complètement un utilisateur et toutes ses données (RGPD Article 17)
 */
export async function deleteUserData(_userId: string, reason?: string): Promise<boolean> {
  try {
    // Appeler l'Edge Function sécurisée pour suppression RGPD du compte courant
    const { error } = await supabase.functions.invoke('gdpr-delete-self', {
      body: { reason }
    });
    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('Erreur lors de la suppression des données:', toError(error).message);
    throw new Error('Impossible de supprimer complètement les données utilisateur');
  }
}

/**
 * Vérifie si l'utilisateur a donné son consentement RGPD
 */
export async function checkUserConsent(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('gdpr_consent')
      .select('consent_given')
      .eq('user_id', userId)
      .eq('consent_given', true)
      .limit(1)
      .maybeSingle();

    if (error) return false;
    return !!data?.consent_given;
  } catch (error) {
    return false;
  }
}

/**
 * Enregistre le consentement utilisateur
 */
export async function recordUserConsent(userId: string, consentData: any): Promise<boolean> {
  try {
    const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : 'server';

    // Enregistrer le consentement dans la table dédiée
    const { error } = await supabase
      .from('gdpr_consent')
      .insert({
        user_id: userId,
        consent_given: !!consentData?.consentGiven,
        consent_type: 'app_usage',
        user_agent: userAgent,
        data_processing_purposes: consentData?.purposes || [],
        consent_version: '1.0'
      });

    if (error) throw error;

    // Audit
    await supabase.rpc('log_security_event', {
      action_type: 'gdpr_consent_given',
      resource_path: '/gdpr/consent',
      user_agent: 'GDPR_CONSENT_SYSTEM'
    });

    return true;
  } catch (error) {
    logger.error("Erreur lors de l'enregistrement du consentement:", toError(error).message);
    return false;
  }
}